import {
  APP_TIMEZONE,
  atualizarTabelaDePrecoSchema,
  criarTabelaDePrecoSchema,
  localDayKey,
} from '@locaveri/shared'
import { LoaderCircle } from 'lucide-react'
import { type ReactElement, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { Campo } from '@/components/form/campo'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { ApiError, errorMessage } from '@/lib/api'
import { aplicarErrosDaApi, aplicarErrosDoZod } from '@/lib/formulario'

import {
  type TabelaDePreco,
  useAtualizarTabelaDePreco,
  useCriarTabelaDePreco,
  vigenciaEmTexto,
} from './api'

/**
 * Criação e edição de uma TABELA de preço — a casca, nunca as tarifas.
 *
 * ---------------------------------------------------------------------------
 * A TABELA NASCE VAZIA, E ISSO É DE PROPÓSITO
 * ---------------------------------------------------------------------------
 * Nenhuma diária entra por aqui. As faixas são gravadas pelo editor de tarifas,
 * uma categoria de cada vez, porque "a diária não sobe ao alongar a locação" é
 * propriedade do CONJUNTO de faixas de uma categoria — misturar os dois
 * formulários faria este diálogo precisar validar uma grade inteira para salvar
 * um nome.
 *
 * ---------------------------------------------------------------------------
 * EXATAMENTE UMA PADRÃO ATIVA POR LOCADORA
 * ---------------------------------------------------------------------------
 * A padrão é a da VITRINE: é ela que precifica todo cliente que não tem tabela
 * negociada. Quem garante a unicidade é um índice único parcial no banco, e a
 * API traduz a colisão num 409 com texto pronto ("desmarque a atual antes de
 * marcar esta"). O serviço NÃO desmarca a outra sozinho — qual tabela vale na
 * vitrine é decisão comercial, e trocá-la em silêncio mudaria o preço de todo
 * mundo sem que ninguém pedisse.
 *
 * Por isso este diálogo faz duas coisas com esse 409: leva a mensagem DA API
 * para a caixinha `padrao`, em vez de espalhá-la pelo alerta do topo. Sem isso
 * a pessoa releria nome e vigência procurando o erro — e reescrever a frase
 * aqui criaria uma segunda versão da mesma regra, que diverge no dia em que o
 * servidor mudar de ideia.
 *
 * ---------------------------------------------------------------------------
 * A CAIXINHA "NO AR" NÃO É UMA PORTA DOS FUNDOS
 * ---------------------------------------------------------------------------
 * `DELETE /tabelas/:id` recusa desativar a tabela da vitrine e a que tem conta
 * negociada pendurada. O `PATCH` que este formulário usa NÃO tem essas guardas:
 * desmarcar "No ar" aqui grava `ativo = false` sem reclamar, e como o índice
 * único é parcial (`WHERE padrao = true AND ativo = true`) o tenant fica sem
 * NENHUMA tabela padrão ativa — a cotação de quem não tem tabela negociada
 * passa a falhar por ausência de dado. O formulário fecha essa porta antes do
 * envio e explica o caminho certo, que é marcar outra tabela como padrão.
 *
 * ---------------------------------------------------------------------------
 * VIGÊNCIA É DATA DE CALENDÁRIO
 * ---------------------------------------------------------------------------
 * `vigenteDe` e `vigenteAte` são `AAAA-MM-DD`, sem hora e sem fuso. Passá-las
 * por `Date` adiantaria um dia em todo fuso negativo — a tabela de março
 * apareceria começando em fevereiro —, então o formulário só as fatia e as
 * devolve como texto.
 */

const CAMPOS = ['nome', 'descricao', 'vigenteDe', 'vigenteAte', 'padrao', 'ativo'] as const

interface FormularioDeTabela {
  nome: string
  descricao: string
  /** `AAAA-MM-DD`. */
  vigenteDe: string
  /** Vazio = sem prazo. */
  vigenteAte: string
  padrao: boolean
  ativo: boolean
}

/**
 * Hoje em `AAAA-MM-DD`, no fuso da OPERAÇÃO.
 *
 * `toISOString().slice(0, 10)` daria o dia UTC — em São Paulo, das 21h em
 * diante, uma tabela criada hoje já nasceria vigente a partir de amanhã. E o
 * relógio do APARELHO também não serve (invariante nº 2): de qualquer fuso à
 * frente do nosso, a tabela entraria em vigor um dia atrasada.
 */
function hojeLocal(): string {
  return localDayKey(new Date(), APP_TIMEZONE)
}

function valoresIniciais(tabela: TabelaDePreco | undefined): FormularioDeTabela {
  return {
    nome: tabela?.nome ?? '',
    descricao: tabela?.descricao ?? '',
    // O `<input type="date">` fica MUDO com um ISO completo: sem o fatiamento o
    // campo abre vazio numa tabela que tem vigência.
    vigenteDe: (tabela?.vigenteDe ?? hojeLocal()).slice(0, 10),
    vigenteAte: (tabela?.vigenteAte ?? '').slice(0, 10),
    padrao: tabela?.padrao ?? false,
    ativo: tabela?.ativo ?? true,
  }
}

/** As duas recusas de `DELETE /tabelas/:id`, aplicadas ao `PATCH`. */
type BloqueioDoNoAr = 'PADRAO' | 'CONTAS_NEGOCIADAS'

/**
 * Frase curta, para o erro DO CONTROLE. O porquê inteiro fica no alerta —
 * a caixinha diz o que está errado, o alerta diz o que isso quebraria.
 */
const MOTIVO_PARA_NAO_TIRAR_DO_AR: Readonly<Record<BloqueioDoNoAr, string>> = {
  PADRAO: 'a tabela da vitrine precisa continuar no ar — marque outra como padrão primeiro',
  CONTAS_NEGOCIADAS:
    'há cliente usando esta tabela como tabela negociada — aponte essas contas para outra antes',
}

/**
 * Por que este formulário não pode tirar a tabela do ar.
 *
 * Olha o estado RESULTANTE, e não a caixinha isolada: marcar `padrao` e
 * desmarcar `ativo` no mesmo envio produziria uma "padrão fora do ar", que o
 * índice único parcial nem enxerga — padrão de ninguém.
 *
 * `null` para tabela nova (nasce ativa) e para tabela que já estava fora do ar:
 * ali não há nada a proteger.
 */
function bloqueioDeTirarDoAr(
  tabela: TabelaDePreco | undefined,
  padrao: boolean,
  ativo: boolean,
): BloqueioDoNoAr | null {
  if (tabela === undefined || !tabela.ativo || ativo) {
    return null
  }

  if (tabela.padrao || padrao) {
    return 'PADRAO'
  }

  return tabela.contasNegociadas > 0 ? 'CONTAS_NEGOCIADAS' : null
}

export interface DialogoDeTabelaProps {
  readonly aberto: boolean
  readonly aoFechar: () => void
  /** `undefined` cria uma tabela nova; preenchido edita a existente. */
  readonly tabela?: TabelaDePreco | undefined
}

export function DialogoDeTabela({ aberto, aoFechar, tabela }: DialogoDeTabelaProps): ReactElement {
  const editando = tabela !== undefined
  const [erroGeral, setErroGeral] = useState<string | null>(null)

  const criar = useCriarTabelaDePreco()
  const atualizar = useAtualizarTabelaDePreco()

  const {
    register,
    handleSubmit,
    setError,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormularioDeTabela>({ defaultValues: valoresIniciais(undefined) })

  // O diálogo é montado uma vez e reaproveitado: sem este efeito, abrir
  // "editar" depois de "nova tabela" mostraria os valores do formulário
  // anterior — inclusive a caixinha de padrão.
  useEffect(() => {
    if (aberto) {
      setErroGeral(null)
      reset(valoresIniciais(tabela))
    }
  }, [aberto, tabela, reset])

  const ocupado = criar.isPending || atualizar.isPending
  const padraoMarcado = watch('padrao')
  const ativoMarcado = watch('ativo')
  const jaEraPadrao = tabela?.padrao ?? false
  // Ao vivo: o aviso aparece no instante em que a caixinha é desmarcada, e não
  // só quando a pessoa já apertou "Salvar".
  const bloqueioDoNoAr = bloqueioDeTirarDoAr(tabela, padraoMarcado, ativoMarcado)

  /**
   * O 409 da tabela padrão é sobre a CAIXINHA, e não sobre o formulário todo.
   * `aplicarErrosDaApi` só espalha `VALIDATION_FAILED` pelos campos; um conflito
   * de estado morreria no alerta do topo, longe do controle que o causou.
   */
  function tratarErroDaApi(causa: unknown): void {
    const geral = aplicarErrosDaApi(causa, setError, CAMPOS)

    if (ApiError.is(causa) && causa.code === 'TABELA_DE_PRECO_PADRAO_DUPLICADA') {
      // A frase é a que o servidor mandou — reescrevê-la aqui seria uma segunda
      // versão da mesma regra, divergindo no dia em que a API mudar de ideia.
      // E sai do alerta do topo: dita duas vezes, ela parece dois problemas.
      setError('padrao', { type: 'server', message: errorMessage(causa) })
      setErroGeral(null)
      return
    }

    setErroGeral(geral)
  }

  const aoEnviar = handleSubmit((valores) => {
    setErroGeral(null)

    const vigenteAte = valores.vigenteAte.trim()
    const descricao = valores.descricao.trim()

    if (tabela !== undefined) {
      // O `PATCH` grava `ativo = false` sem reclamar — as guardas moram só no
      // `DELETE`. Recusar aqui, sobre os valores ENVIADOS (e não sobre o
      // `watch`, que pode estar um render atrás), é o que impede o tenant de
      // ficar sem tabela padrão ativa por um clique numa caixinha.
      const bloqueio = bloqueioDeTirarDoAr(tabela, valores.padrao, valores.ativo)

      if (bloqueio !== null) {
        setError('ativo', { type: 'validate', message: MOTIVO_PARA_NAO_TIRAR_DO_AR[bloqueio] })
        return
      }

      // Campo vazio vira `null`, e não ausência: `undefined` significa "não
      // mexa", e apagar a descrição precisa APAGAR a descrição. O mesmo vale
      // para o fim da vigência — limpá-lo é dizer "sem prazo".
      const entrada = {
        nome: valores.nome,
        descricao: descricao.length > 0 ? descricao : null,
        vigenteDe: valores.vigenteDe,
        vigenteAte: vigenteAte.length > 0 ? vigenteAte : null,
        padrao: valores.padrao,
        ativo: valores.ativo,
      }

      const validado = atualizarTabelaDePrecoSchema.safeParse(entrada)

      if (!validado.success) {
        setErroGeral(aplicarErrosDoZod(validado.error, setError, CAMPOS))
        return
      }

      // `validado.data`: este contrato não transforma nada em `bigint` nem em
      // `Date` — a vigência é texto de calendário e o nome só perde espaços em
      // branco. É o oposto dos schemas de dinheiro, onde só a ENTRADA viaja.
      atualizar.mutate(
        { id: tabela.id, dados: validado.data },
        {
          onSuccess: (salva) => {
            toast.success('Tabela atualizada', {
              description: `${salva.nome} · ${vigenciaEmTexto(salva.vigenteDe, salva.vigenteAte)}`,
            })
            aoFechar()
          },
          onError: tratarErroDaApi,
        },
      )
      return
    }

    const entrada = {
      nome: valores.nome,
      ...(descricao.length > 0 ? { descricao } : {}),
      vigenteDe: valores.vigenteDe,
      ...(vigenteAte.length > 0 ? { vigenteAte } : {}),
      padrao: valores.padrao,
      ativo: true,
    }

    const validado = criarTabelaDePrecoSchema.safeParse(entrada)

    if (!validado.success) {
      setErroGeral(aplicarErrosDoZod(validado.error, setError, CAMPOS))
      return
    }

    criar.mutate(validado.data, {
      onSuccess: (criada) => {
        toast.success(`Tabela ${criada.nome} criada`, {
          description:
            'Ela nasce sem tarifa nenhuma. Preencha as faixas de cada categoria no editor de ' +
            'tarifas — sem tarifa, a categoria não é cotável nesta tabela.',
        })
        aoFechar()
      },
      onError: tratarErroDaApi,
    })
  })

  return (
    <Dialog
      open={aberto}
      onOpenChange={(proximo) => {
        if (!proximo) {
          aoFechar()
        }
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editando ? 'Editar tabela de preço' : 'Nova tabela de preço'}</DialogTitle>
          <DialogDescription>
            {editando
              ? 'Nome, vigência e situação. As diárias de cada faixa ficam no editor de tarifas.'
              : 'A tabela nasce vazia: as diárias por faixa entram depois, uma categoria de cada vez.'}
          </DialogDescription>
        </DialogHeader>

        <form
          noValidate
          onSubmit={(evento) => {
            void aoEnviar(evento)
          }}
          className="space-y-4"
        >
          {erroGeral === null ? null : (
            <Alert variant="danger">
              <AlertDescription>{erroGeral}</AlertDescription>
            </Alert>
          )}

          <Campo
            rotulo="Nome"
            obrigatorio
            erro={errors.nome?.message}
            dica="Como a tabela aparece na lista e no contrato. Ex.: “Balcão 2026” ou “Corporativo ACME”."
          >
            {(campo) => (
              <Input
                {...campo}
                {...register('nome', { required: 'informe o nome da tabela' })}
                maxLength={120}
                disabled={ocupado}
              />
            )}
          </Campo>

          <div className="grid gap-4 sm:grid-cols-2">
            <Campo
              rotulo="Vigente de"
              obrigatorio
              erro={errors.vigenteDe?.message}
              dica="Primeiro dia em que esta tabela pode cotar."
            >
              {(campo) => (
                <Input
                  {...campo}
                  {...register('vigenteDe', { required: 'informe o início da vigência' })}
                  type="date"
                  className="tabular-money"
                  disabled={ocupado}
                />
              )}
            </Campo>

            <Campo
              rotulo="Vigente até"
              erro={errors.vigenteAte?.message}
              dica="Em branco = sem prazo. É o caso da tabela de balcão, que vale até alguém trocá-la."
            >
              {(campo) => (
                <Input
                  {...campo}
                  {...register('vigenteAte')}
                  type="date"
                  className="tabular-money"
                  disabled={ocupado}
                />
              )}
            </Campo>
          </div>

          <Campo
            rotulo="Descrição"
            erro={errors.descricao?.message}
            dica="Para quem esta tabela existe. Quem abrir a lista daqui a seis meses vai ler isto."
          >
            {(campo) => (
              <Textarea
                {...campo}
                {...register('descricao')}
                rows={3}
                maxLength={2000}
                disabled={ocupado}
              />
            )}
          </Campo>

          <fieldset className="space-y-2" disabled={ocupado}>
            <legend className="text-sm font-medium text-foreground">Situação</legend>

            <label
              className="flex cursor-pointer items-start gap-2 rounded-lg border border-border
                bg-surface p-3 text-sm"
            >
              <Checkbox {...register('padrao')} className="mt-0.5" />
              <span className="min-w-0">
                <span className="block font-medium text-foreground">
                  Tabela da vitrine (padrão)
                </span>
                <span className="block text-xs text-fg-secondary">
                  É ela que precifica todo cliente sem tabela negociada. Existe exatamente uma ativa
                  por locadora.
                </span>
              </span>
            </label>

            {errors.padrao?.message === undefined ? null : (
              <p className="text-xs text-danger-fg">{errors.padrao.message}</p>
            )}

            {editando ? (
              <>
                <label
                  className="flex cursor-pointer items-start gap-2 rounded-lg border border-border
                    bg-surface p-3 text-sm"
                >
                  <Checkbox {...register('ativo')} className="mt-0.5" />
                  <span className="min-w-0">
                    <span className="block font-medium text-foreground">No ar</span>
                    <span className="block text-xs text-fg-secondary">
                      Desmarcada, a tabela para de cotar. Nada é apagado: toda cotação já emitida
                      continua explicando o preço que cobrou.
                    </span>
                  </span>
                </label>

                {errors.ativo?.message === undefined ? null : (
                  <p className="text-xs text-danger-fg">{errors.ativo.message}</p>
                )}
              </>
            ) : null}
          </fieldset>

          {bloqueioDoNoAr === 'PADRAO' ? (
            <Alert variant="warning">
              <AlertTitle>A tabela da vitrine não sai do ar por aqui</AlertTitle>
              <AlertDescription>
                Sem nenhuma tabela padrão ativa, quem não tem tabela negociada fica sem preço — e a
                cotação passa a falhar por ausência de dado, que é a forma mais confusa de um
                sistema de preço quebrar. Marque outra tabela como padrão e volte para tirar esta.
              </AlertDescription>
            </Alert>
          ) : bloqueioDoNoAr === 'CONTAS_NEGOCIADAS' ? (
            <Alert variant="warning">
              <AlertTitle>
                {tabela?.contasNegociadas === 1
                  ? 'Um cliente usa esta tabela como tabela negociada'
                  : `${String(tabela?.contasNegociadas ?? 0)} clientes usam esta tabela como tabela negociada`}
              </AlertTitle>
              <AlertDescription>
                Tirá-la do ar jogaria essas contas de volta na tabela cheia sem aviso, e a primeira
                notícia seria uma fatura mais cara. Aponte-as para outra tabela antes.
              </AlertDescription>
            </Alert>
          ) : null}

          {padraoMarcado && !jaEraPadrao ? (
            <Alert variant="info">
              <AlertTitle>Só existe uma tabela padrão ativa</AlertTitle>
              <AlertDescription>
                Se já houver outra marcada, o servidor recusa esta — desmarque a atual antes de
                salvar. A troca não acontece sozinha de propósito: mudar a tabela da vitrine muda o
                preço de todo cliente que não tem tabela negociada.
              </AlertDescription>
            </Alert>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={aoFechar} disabled={ocupado}>
              Cancelar
            </Button>
            <Button type="submit" disabled={ocupado}>
              {ocupado ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
              {editando ? 'Salvar alterações' : 'Criar tabela'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
